<template>
  <div class="content">
    <div class="content-wrap">
      <!-- Nagłówek formularza -->
      <h3>{{ t('user-edit').toUpperCase() }}</h3>
      <div class="card-body">
        <!-- Formularz z walidacją (vee-validate) -->
        <Form
          @submit="onSubmit"
          :validation-schema="schema"
          v-slot="{ errors, isSubmitting }"
          :keepValues="false"
        >
          <div class="column d-flex justify-content-center user-data">
            <!-- LEWA KOLUMNA -->
            <div class="col-sm-6">
              <!-- Pole: nazwa użytkownika -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('username') }}:</label>
                <div class="col-sm-8">
                  <Field
                    name="userName"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.userName }"
                    v-model="userData.username"
                  />
                  <div class="invalid-feedback">{{ errors.userName }}</div>
                </div>
              </div>
              <!-- Pole: imię -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('name') }}:</label>
                <div class="col-sm-8">
                  <Field
                    name="firstName"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.firstName }"
                    v-model="userData.firstName"
                  />
                  <div class="invalid-feedback">{{ errors.firstName }}</div>
                </div>
              </div>
              <!-- Pole: nazwisko -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('lastname') }}:</label>
                <div class="col-sm-8">
                  <Field
                    name="lastName"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.lastName }"
                    v-model="userData.lastName"
                  />
                  <div class="invalid-feedback">{{ errors.lastName }}</div>
                </div>
              </div>
              <!-- Pole: email -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('email-address') }}:</label>
                <div class="col-sm-8">
                  <Field
                    name="email"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.email }"
                    v-model="userData.email"
                  />
                  <div class="invalid-feedback">{{ errors.email }}</div>
                </div>
              </div>
              <!-- Telefon - kierunkowy i nr główny -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('phone') }}:</label>
                <div class="col-sm-2">
                  <!-- Kierunkowy (readonly) -->
                  <Field
                    name="phoneNumberDir"
                    type="text"
                    class="form-control"
                    :disabled="true"
                    v-model="userData.phoneNumberDir"
                  />
                </div>
                <div class="col-sm-6">
                  <!-- Numer główny -->
                  <Field
                    name="phoneNumber"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.phoneNumber }"
                    v-model="userData.phoneNumber"
                  />
                  <div class="invalid-feedback">
                    {{ errors.phoneNumber }}
                  </div>
                </div>
              </div>
            </div>
            <!-- PRAWA KOLUMNA -->
            <div class="col-sm-6">
              <!-- Pole: hasło (opcjonalne) -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('password') }}:</label>
                <div class="col-sm-8">
                  <Field
                      name="password"
                      type="password"
                      class="form-control"
                      :class="{ 'is-invalid': errors.password }"
                      v-model="userData.password"
                      autocomplete="new-password"
                  />
                  <div class="invalid-feedback">{{ errors.password }}</div>
                </div>
              </div>

              <!-- Pole: powtórz hasło -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('repeat-password') }}:</label>
                <div class="col-sm-8">
                  <Field
                      name="confirmPassword"
                      type="password"
                      class="form-control"
                      :class="{ 'is-invalid': errors.confirmPassword }"
                      v-model="userData.confirmPassword"
                      autocomplete="new-password"
                  />
                  <div class="invalid-feedback">{{ errors.confirmPassword }}</div>
                </div>
              </div>
              <!-- Uprawnienia (rola) -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('permissions') }}:</label>
                <div class="col-sm-8">
                  <Field
                    name="roles"
                    as="select"
                    class="form-control"
                    :class="{ 'is-invalid': errors.roles }"
                    v-model="selectedRoleId"
                  >
                    <!--
                      Grupy zawierające ROLE_17/18/19 są ukryte dla adminów, którzy
                      sami nie posiadają tych uprawnień — żeby nie dało się eskalować
                      uprawnień przez wybór gotowej grupy. Wyjątek: aktualnie przypisana
                      grupa (currentRoleId) jest zawsze pokazana, żeby nie znikała przy
                      edycji.
                    -->
                    <option
                      v-for="role in assignableRoles"
                      :key="role.id"
                      :value="role.id"
                    >
                      {{ tdb(role.name) }}
                    </option>
                  </Field>
                  <div class="invalid-feedback">{{ errors.roles }}</div>
                </div>
              </div>
              <!-- Opis -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('description') }}:</label>
                <div class="col-sm-8">
                  <Field
                    name="description"
                    type="textarea"
                    class="form-control"
                    v-model="userData.description"
                  />
                  <div class="invalid-feedback">{{ errors.description }}</div>
                </div>
              </div>
              <!-- Status: aktywny/nieaktywny (checkbox) -->
              <div class="form-group row">
                <label class="col-sm-4 col-form-label">{{ t('status') }}:</label>
                <div class="col-sm-8">
                  <Field
                    name="status"
                    type="checkbox"
                    :value="true"
                    v-model="userData.enabled"
                  />
                  <div class="invalid-feedback">{{ errors.status }}</div>
                </div>
              </div>
              <!-- Grupa użytkowników (opcjonalna). Grupami zarządza się w zakładce „Grupy użytkowników”. -->
              <div class="form-group row" v-if="userGroupsAvailable.length > 0">
                <label class="col-sm-4 col-form-label" for="edit-user-group">{{ t('user-group') }}:</label>
                <div class="col-sm-8">
                  <select id="edit-user-group" v-model="selectedUserGroupId" class="form-control">
                    <option value="">{{ t('user-group-none') }}</option>
                    <option v-for="group in userGroupsAvailable" :key="group.id" :value="group.id">
                      {{ group.name }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <!-- Przycisk Zapisz i Anuluj + komunikaty -->
          <div class="form-group row d-flex flex-column align-items-center actions">
            <div class="col-auto" v-if="processError">
              <div class="process-error form-control">{{ processError }}</div>
            </div>
            <div class="col-auto" v-if="processSuccess">
              <div class="process-success form-control">
                {{ processSuccess }}
              </div>
            </div>
            <div class="col-auto buttons">
              <button
                type="button"
                @click="goBack"
                class="btn button-add-user"
              >
                {{ t('cancel') }}
              </button>
              <button
                class="btn button-save"
                :disabled="isSubmitting"
              >
                <span
                  v-show="isSubmitting"
                  class="spinner-border spinner-border-sm mr-1"
                ></span>
                {{ t('save').toUpperCase() }}
              </button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  </div>
</template>

<script setup>
import moment from "moment";
import { Form, Field } from "vee-validate";
import * as Yup from "yup";
import { useI18n } from "vue-i18n";

moment.locale("pl");

const { t } = useI18n();

// Yup - schemat walidacji (dynamicznie tłumaczony!)
const schema = Yup.object().shape({
  userName: Yup.string().required(t('user-username-required')),
  firstName: Yup.string().required(t('user-name-required')),
  lastName: Yup.string().required(t('user-lastname-required')),
  email: Yup.string()
    .required(t('user-email-required'))
    .email(t('incorrect-email-address')),
  phoneNumber: Yup.string(), // opcjonalny — nie każdy użytkownik ma służbowy numer
  roles: Yup.string().required(t('user-permissions-required')),
  description: Yup.string(),
  status: Yup.bool(),
  password: Yup.string(),
  confirmPassword: Yup.string().oneOf(
    [Yup.ref('password'), null],
    t('passwords-are-not-the-same')
  ),
});
</script>

<script>
import { ref } from "vue";
import Users from "@/services/Users";
import UserGroupsService from "@/services/UserGroupsService";
import { useUserStore } from "@/stores/user.store";

export default {
  props: {
    userId: { required: true }, // ID edytowanego użytkownika
  },
  name: "EditUser",
  computed: {
    // Store przez getter — w SFC z `<script setup>` druga sekcja `<script>` (Options API)
    // nie wykonuje swojego `setup()`, więc zwrócenie store'a tą drogą daje `undefined`
    // w `this`. Computed wywoła `useUserStore()` przy każdym dostępie (singleton).
    userStore() {
      return useUserStore();
    },
    // Grupy ról, do których admin może przypisać użytkownika. Wyklucza grupy
    // zawierające uprawnienia chronione (ROLE_17/18/19), jeśli admin sam ich nie posiada.
    // Aktualnie wybrana grupa (selectedRoleId) jest zawsze widoczna — żeby formularz
    // nie tracił przypisania przy edycji innych pól.
    assignableRoles() {
      return (this.rolesAvailable || []).filter((role) => {
        if (Number(role?.id) === Number(this.selectedRoleId)) {
          return true;
        }
        return this.userStore.canAssignRoleGroup(role);
      });
    },
  },
  data() {
    return {
      loading: false, // do spinnerów
      users: [], // nieużywane tu
      rolesAvailable: [], // lista ról do wyboru
      error: null,
      moment: moment,
      getPasswordLength: 14, // do wyświetlania/obsługi długości hasła?
      processError: false, // komunikat błędu zapisu
      processSuccess: false, // komunikat sukcesu
      userData: {}, // główny obiekt formularza (zassany z API)
      form: {
        password: ref(""),
        confirmPassword: ref(""),
        proposed: ref(""),
        userName: ref(""),
      },
      selectedRoleId: null, // wybrana rola (pojedyncza)
      userGroupsAvailable: [], // grupy użytkowników do wyboru
      userGroupsLoaded: false, // czy lista grup się wczytała (bez niej nie wysyłamy zmiany grupy)
      selectedUserGroupId: "", // "" = bez grupy
    };
  },
  created() {
    // Pobierz role z API i zapisz je do rolesAvailable
    Users.getRolesList().then((data) => {
      for (const item in data) {
        this.rolesAvailable.push(data[item]);
      }
    });

    // Pobierz grupy użytkowników do wyboru
    UserGroupsService.getAll()
      .then((groups) => {
        this.userGroupsAvailable = Array.isArray(groups) ? groups : [];
        this.userGroupsLoaded = true;
      })
      .catch((error) => {
        console.error("Error fetching user groups:", error);
        this.userGroupsAvailable = [];
      });

    // Pobierz dane użytkownika do edycji
    Users.fetchUser(this.userId)
        .then((data) => {
          this.userData = data;

          // Rozdziel numer telefonu na kierunkowy i numer
          if (data.phoneNumber && data.phoneNumber.startsWith("+48")) {
            this.userData.phoneNumberDir = "+48";
            this.userData.phoneNumber = data.phoneNumber.slice(3);
          } else {
            this.userData.phoneNumberDir = "+48";
            this.userData.phoneNumber = data.phoneNumber || "";
          }

          // Predefiniuj hasło (nie wyświetlamy prawdziwego!)
          this.userData.password = "";
          this.userData.confirmPassword = ""; // <-- tutaj
          this.selectedRoleId = data.roles[0]?.id || null;
          this.selectedUserGroupId = data.userGroup?.id ?? "";

          this.userData.permissions = data.roles
              .map((role) => role.name)
              .join(",");
          this.form.userName = data.username;
        })
        .catch((error) => {
        console.log(error);
        this.processError = error;
      });
  },
  methods: {
    goBack() {
      this.$emit("goBack"); // powrót do listy użytkowników
    },
    onSubmit(values) {
      this.processSuccess = null;
      this.processError = null;

      // Jeśli użytkownik nie zmienił hasła - nie nadpisuj hasła w API
      const newPassword = values.password?.trim();

      // Złóż pełny numer telefonu do API (numer opcjonalny — pusty wysyłamy jako "", a nie sam prefiks)
      const fullPhoneNumber = values.phoneNumber
        ? (this.userData.phoneNumberDir || "+48") + values.phoneNumber
        : "";

      // Zbuduj obiekt do wysłania do API
      const sendUser = {
        username: values.userName,
        password: newPassword ? newPassword : null,
        enabled: values.status ? values.status : false,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: fullPhoneNumber,
        company: "",
        description: values.description,
        sendUserLogsToServer: this.userData.sendUserLogsToServer,
        roles: [this.selectedRoleId], // Tylko jeden ID roli!
      };

      // Grupa użytkowników: 0 = bez grupy. Gdy lista grup się nie wczytała, nie ruszamy przypisania.
      if (this.userGroupsLoaded) {
        sendUser.userGroupId = this.selectedUserGroupId === "" ? 0 : Number(this.selectedUserGroupId);
      }

      // Aktualizuj użytkownika
      Users.updateUser(this.userId, sendUser)
        .then(() => {
          this.goBack();
        })
        .catch((error) => {
          console.log(error);
          this.processError = error;
        });
    },
  },
};
</script>

<style scoped>
.col-form-label {
  text-align: right;
}
.process-error {
  color: darkred;
}

.process-success {
  color: var(--status-ok);
}

.content {
  width: 100%;
  min-height: 270px;
}

h3 {
  padding: 15px 25px;
  background-color: var(--surface-primary);
}

.card-body .column {
  padding: 20px 30px;
}

.card-body .column > div {
  padding: 5px 20px;
}
.user-table {
  width: 100%;
  font-family: "Ubuntu", sans-serif;
  font-style: normal;
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-xl);
  color: var(--text-primary);
}

.user-table th {
  background: var(--surface-primary);
  font-size: var(--font-size-lg);
  color: var(--text-primary);
  border-left: 1px solid rgb(214, 214, 215);
  border-bottom: 1px solid rgb(214, 214, 215);
}

.user-table td {
  background: rgb(214, 214, 215);
  font-size: var(--font-size-md);
  color: dimgrey;
  border-left: 1px solid var(--border-default);
}
.actions {
  margin: 10px 0;
}
.actions button {
  margin: 0 5px;
}
.user-data {
  border-bottom: 1px solid var(--border-default);
}
.invalid-feedback {
  font-size: var(--font-size-xs);
}
input[type="checkbox"] {
  margin-top: 13px;
}

.buttons {
  margin-top: .25rem;
  margin-bottom: 1rem;
}

.buttons button {
  padding: 0.5rem 2rem 0.5rem 2rem;
}

.button-save {
  border: 1px solid var(--status-ok) !important;
  border-radius: 4px;
}

.button-save:hover {
  background-color: var(--status-ok) !important;
  border: 1px solid var(--status-ok) !important;
}

.button-add-user {
  border-radius: 4px;
}

.button-add-user:hover {
  background-color: var(--border-strong);
}
</style>
